import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Badge,
  DataTable,
  Field,
  LoadState,
  Page,
  Panel,
  Stats,
  dateTime,
  money,
  useAdminData,
  useFinanceData,
} from "layouts/finance/shared";
import {
  allowedIpList,
  financeApi,
  merchantAdminApi,
  totalCount,
  walletParams,
  walletRows,
} from "services/FinanceService";
import useAdminMutation from "./useAdminMutation";

const copyToClipboard = async (value, label) => {
  try {
    await navigator.clipboard.writeText(String(value ?? ""));
    toast.success(`${label} copied to clipboard.`);
  } catch (error) {
    toast.error(`Unable to copy ${label.toLowerCase()}.`);
  }
};

export default function Wallets() {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [walletType, setWalletType] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [activeMerchant, setActiveMerchant] = useState("");
  const [ipDraft, setIpDraft] = useState("");

  const directory = useFinanceData(
    "/wallets",
    walletParams(page, size, walletType)
  );
  const wallet = useAdminData(
    financeApi,
    `/merchants/${encodeURIComponent(activeMerchant)}/wallet`,
    undefined,
    { enabled: Boolean(activeMerchant) }
  );
  const allowlist = useAdminData(
    merchantAdminApi,
    `/${encodeURIComponent(activeMerchant)}/allowed-ips`,
    undefined,
    { enabled: Boolean(activeMerchant) }
  );
  const mutation = useAdminMutation(() => {
    allowlist.refresh();
  });

  useEffect(() => {
    setIpDraft(allowedIpList(allowlist.data).join("\n"));
  }, [allowlist.data]);

  const rows = walletRows(directory.data);
  const total = totalCount(directory.data);
  const hasNext =
    total != null
      ? page + 1 < Math.ceil(total / size)
      : rows.length >= size;

  const submitAllowlist = () => {
    const allowedIps = ipDraft
      .split("\n")
      .map((entry) => entry.trim())
      .filter(Boolean);
    mutation.submit(
      merchantAdminApi,
      `/${encodeURIComponent(activeMerchant)}/allowed-ips`,
      { method: "POST", data: { allowedIps } },
      undefined,
      "Allowed IPs updated successfully."
    );
  };

  return (
    <Page
      eyebrow="Administration"
      title="Wallets"
      description="Platform wallet directory, merchant balances, and IP allowlist overrides."
      actions={
        <button
          className="fin-button secondary"
          onClick={() => {
            directory.refresh();
            wallet.refresh();
            allowlist.refresh();
          }}
        >
          Refresh
        </button>
      }
    >
      <Panel>
        <div className="toolbar">
          <Field
            label="Wallet type"
            name="walletType"
            value={walletType}
            onChange={(e) => {
              setWalletType(e.target.value.trim());
              setPage(0);
            }}
            placeholder="e.g. MERCHANT_MAIN"
          />
          <Field
            label="Page size"
            name="size"
            value={size}
            options={["10", "20", "50", "100"]}
            onChange={(e) => {
              setSize(Number(e.target.value));
              setPage(0);
            }}
          />
        </div>
      </Panel>

      <LoadState {...directory} />
      {directory.data && (
        <Panel
          title="Platform wallet directory"
          subtitle="Every wallet held by the platform, including merchant and sub-user wallets."
        >
          <DataTable
            rowKey="walletId"
            rows={rows}
            columns={[
              { key: "walletId", label: "Wallet ID" },
              { key: "merchantId", label: "Merchant ID" },
              { key: "walletType", label: "Wallet type" },
              { key: "accountNumber", label: "Account number" },
              { key: "bankName", label: "Bank" },
              {
                key: "balance",
                label: "Balance",
                render: (row) => money(row.balance),
              },
              {
                key: "status",
                label: "Status",
                render: (row) => <Badge value={row.status} />,
              },
            ]}
          />
          <div className="pagination">
            <span>
              {total != null
                ? `${total} wallets`
                : `${rows.length} wallets on this page`}{" "}
              · Page {page + 1}
            </span>
            <button
              className="fin-button secondary"
              disabled={page === 0 || directory.loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <button
              className="fin-button secondary"
              disabled={directory.loading || !hasNext}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </Panel>
      )}

      <Panel>
        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault();
            setActiveMerchant(merchantId.trim());
          }}
        >
          <Field
            label="Merchant ID"
            name="merchantId"
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
            placeholder="e.g. 251"
            required
          />
          <button
            className="fin-button"
            type="submit"
            disabled={!merchantId.trim()}
          >
            Load merchant wallet
          </button>
        </form>
      </Panel>

      {activeMerchant && (
        <>
          <LoadState {...wallet} />
          {wallet.data && (
            <Panel
              title={`Merchant wallet · ${wallet.data.merchantId || activeMerchant}`}
              subtitle="Operational wallet for the selected merchant."
            >
              <Stats
                items={[
                  ["Available balance", money(wallet.data.balance)],
                  ["Wallet status", <Badge value={wallet.data.status} />],
                  [
                    "Last updated",
                    wallet.data.updatedAt
                      ? dateTime(wallet.data.updatedAt)
                      : "—",
                  ],
                ]}
              />
              <dl className="detail-grid" style={{ marginTop: 16 }}>
                {[
                  ["Wallet ID", wallet.data.walletId],
                  ["Account number", wallet.data.accountNumber],
                  ["Bank name", wallet.data.bankName],
                  ["Bank code", wallet.data.bankCode],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value ?? "—"}</dd>
                  </div>
                ))}
              </dl>
              {wallet.data.accountNumber && (
                <button
                  className="fin-button secondary"
                  style={{ marginTop: 16 }}
                  onClick={() =>
                    copyToClipboard(wallet.data.accountNumber, "Account number")
                  }
                >
                  Copy account number
                </button>
              )}
            </Panel>
          )}

          <Panel
            title="IP allowlist override"
            subtitle="Administrative override of a merchant's API allowlist; cooldown limits do not apply."
          >
            <LoadState {...allowlist} />
            {allowlist.data && (
              <>
                <div className="toolbar" style={{ marginBottom: 16 }}>
                  <span className="report-period">
                    Unrestricted:{" "}
                    {allowlist.data.unrestricted ? "Yes" : "No"} · Changes used:{" "}
                    {allowlist.data.changeCount ?? 0} of{" "}
                    {allowlist.data.maxChangesPerWindow ?? 0}
                  </span>
                </div>
                <label className="fin-field">
                  <span>Allowed IPs (one per line)</span>
                  <textarea
                    rows={5}
                    value={ipDraft}
                    onChange={(e) => setIpDraft(e.target.value)}
                    placeholder="102.89.47.10&#10;197.210.8.0/24"
                  />
                </label>
                <div className="toolbar" style={{ marginTop: 16 }}>
                  <button
                    className="fin-button"
                    disabled={mutation.busy}
                    onClick={submitAllowlist}
                  >
                    {mutation.busy ? "Saving…" : "Save allowlist"}
                  </button>
                  <button
                    className="fin-button secondary"
                    disabled={mutation.busy}
                    onClick={() => allowlist.refresh()}
                  >
                    Discard changes
                  </button>
                </div>
                {mutation.error && (
                  <p className="notice error" role="alert">
                    {mutation.error}
                  </p>
                )}
              </>
            )}
          </Panel>
        </>
      )}
    </Page>
  );
}
